import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, Instagram } from "lucide-react";

const footerLinks = {
  producto: [
    { label: "Colecciones", href: "#colecciones" },
    { label: "Experiencia QR", href: "#experiencia" },
    { label: "Bienestar", href: "#bienestar" },
  ],
  soporte: [
    { label: "Preguntas Frecuentes", href: "#faq" },
    { label: "Contacto", href: "mailto:hola@serena.mx" },
    { label: "Envios", href: "#faq" },
  ],
  legal: [
    { label: "Privacidad", href: "/privacidad" },
    { label: "Términos", href: "/terminos" },
    { label: "Eliminar Cuenta", href: "/eliminar-cuenta" },
    { label: "Cookies", href: "#" },
  ],
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Footer() {
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    // Check and add hCaptcha token manually if missing
    if (!data['h-captcha-response'] && window.hcaptcha) {
      try {
        const token = window.hcaptcha.getResponse();
        if (token) {
          data['h-captcha-response'] = token;
        }
      } catch (err) {
        console.error('Error getting hCaptcha token:', err);
      }
    }

    const json = JSON.stringify(data);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: json
      });
      const result = await res.json();
      if (result.success) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch (err) {
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative border-t border-white/5 pt-20 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16"
        >
          {/* Brand + waitlist */}
          <motion.div variants={fadeUp} className="md:col-span-5">
            <span className="font-serif text-3xl text-[#D4AF37] block mb-4">
              Serena
            </span>
            <p className="text-sm text-white/40 leading-relaxed mb-6 max-w-sm">
              Velas aromaticas premium con experiencia de bienestar digital.
              Hecho en Mexico con proposito.
            </p>

            {/* Store badges */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <a
                href="https://play.google.com/store/apps/details?id=mx.com.serenacandles.app&hl=es_MX"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-google-play-badge"
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 hover:border-[#D4AF37]/50 px-3 py-1.5 transition-all duration-300 group"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#D4AF37] group-hover:scale-110 transition-transform" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.3 2.3-8.636-8.632z"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-[8px] uppercase tracking-wider text-white/40 leading-none">Disponible en</span>
                  <span className="text-xs font-medium text-white leading-tight">Google Play</span>
                </div>
              </a>
              <span
                data-testid="footer-app-store-badge"
                className="flex items-center gap-1.5 bg-white/5 border border-white/10 px-3 py-1.5 opacity-50 cursor-default"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white/40" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-[8px] uppercase tracking-wider text-white/40 leading-none">Próximamente</span>
                  <span className="text-xs font-medium text-white/40 leading-tight">App Store</span>
                </div>
              </span>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
              data-testid="footer-waitlist-form"
            >
              <input type="hidden" name="access_key" value="ae7f687c-709c-49f9-9f94-5425e531d2fd" />
              <input type="hidden" name="subject" value="Nuevo Registro - Lista de Espera Serena" />
              <input type="hidden" name="from_name" value="Serena Footer" />

              <div className="flex gap-2 w-full">
                <input
                  type="email"
                  name="email"
                  placeholder="tu@email.com"
                  required
                  className="flex-1 bg-transparent border-b border-white/20 focus:border-[#D4AF37] px-0 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none transition-colors duration-300"
                  data-testid="footer-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-black px-6 py-2 text-xs uppercase tracking-wider font-medium hover:bg-[#C5A059] transition-all duration-300 disabled:opacity-50"
                  data-testid="footer-submit-btn"
                >
                  {loading ? "..." : "Unirme"}
                </button>
              </div>
              <div className="h-captcha" data-captcha="true"></div>
            </form>
            {status === "success" && (
              <p className="text-[#D4AF37] text-xs mt-2" data-testid="footer-success-msg">
                Te uniste a la lista de espera.
              </p>
            )}
            {status === "error" && (
              <p className="text-red-400 text-xs mt-2" data-testid="footer-error-msg">
                Error. Verifica el captcha e intenta de nuevo.
              </p>
            )}
          </motion.div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <motion.div
              key={category}
              variants={fadeUp}
              className="md:col-span-2"
            >
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-white/30 mb-5">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("/") ? (
                      <Link
                        to={link.href}
                        className="text-sm text-white/50 hover:text-[#D4AF37] transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-white/50 hover:text-[#D4AF37] transition-colors duration-300"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-white/5 gap-4">
          <p className="text-xs text-white/20">
            &copy; 2025 Serena. Todos los derechos reservados. Hecho en Mexico.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://www.instagram.com/serena_candlesmx"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram de Serena"
              className="text-white/40 hover:text-[#D4AF37] transition-colors duration-300"
            >
              <Instagram size={18} />
            </a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="w-10 h-10 flex items-center justify-center border border-white/10 hover:border-[#D4AF37]/50 transition-colors duration-300"
              data-testid="scroll-to-top"
              aria-label="Volver arriba"
            >
              <ArrowUp size={16} className="text-white/40" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
